import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listUnifiedSupportInbox } from "@/lib/inbox.functions";
import { linkSupportInbox, processSupportInbox } from "@/lib/support.functions";
import { PageHeader } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/inbox")({
  head: () => ({
    meta: [{ title: "Inkorg – Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: InboxPage,
});
const labels = {
  pending: "Väntar på innehåll",
  access_blocked: "Receiving-åtkomst blockerad",
  retryable_error: "Hämtning misslyckades",
  processing: "Hämtning pågår",
  complete: "Innehåll hämtat",
};
function InboxPage() {
  const list = useServerFn(listUnifiedSupportInbox);
  const link = useServerFn(linkSupportInbox);
  const fetchContent = useServerFn(processSupportInbox);
  const query = useQuery({
    queryKey: ["support-inbox"],
    queryFn: () => list({}),
    refetchInterval: 30000,
  });
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"pending" | "all" | "legacy">("pending");
  const [busy, setBusy] = useState<string | null>(null);
  async function action(emailId: string, fetch: boolean) {
    setBusy(emailId);
    try {
      await (fetch ? fetchContent({ data: { emailId } }) : link({ data: { emailId } }));
      toast.success(
        fetch ? "Mejlkropp och tråd sparade" : "Ärende skapat från metadata; inget svar skickat",
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Åtgärden misslyckades");
    } finally {
      await qc.invalidateQueries({ queryKey: ["support-inbox"] });
      await qc.invalidateQueries({ queryKey: ["support"] });
      setBusy(null);
    }
  }
  if (query.isPending) return <p>Läser inkorgen…</p>;
  if (query.error) return <p role="alert">{query.error.message}</p>;
  const data = query.data;
  const rows =
    filter === "pending" ? data.queue.filter((i) => i.fetchStatus !== "complete") : data.queue;
  return (
    <div className="space-y-5">
      <PageHeader title="Inkorg" />
      <p className="text-sm text-muted-foreground">
        Signerade inkommande mejl sparas här innan kvittering. Metadata kan visas medan mejlkroppen
        väntar på Receiving-åtkomst. Alla kundsvar behöver separat granskning av ett exakt
        svarsutkast.
      </p>
      <div className="flex gap-3 flex-wrap">
        {(["pending", "all", "legacy"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded border px-3 py-2 ${filter === f ? "bg-primary text-primary-foreground" : ""}`}
          >
            {f === "pending"
              ? "Väntande innehåll"
              : f === "all"
                ? "Signerad inkorg"
                : "Tidigare inkorg"}
          </button>
        ))}
        <button
          className="underline"
          onClick={() => qc.invalidateQueries({ queryKey: ["support-inbox"] })}
        >
          Uppdatera
        </button>
        <Link to="/admin/support" search={{ ticket: undefined }} className="underline">
          Öppna support och godkännandekö
        </Link>
      </div>
      {filter === "legacy" ? (
        <section>
          <p className="text-sm mb-3">
            Detta är äldre inkorgsposter. Deras granskningsstatus godkänner inget kundsvar i den nya
            supportkön.
          </p>
          {data.legacy.length === 0 ? (
            <p>Inga äldre poster.</p>
          ) : (
            data.legacy.map((m) => (
              <article key={m.id} className="rounded border p-4 mb-3">
                <b>{m.subject || "(ingen rubrik)"}</b>
                <p>
                  {m.from_address} · {m.status}
                </p>
                <pre className="whitespace-pre-wrap break-words font-sans">
                  {m.text_body || "Innehåll saknas"}
                </pre>
              </article>
            ))
          )}
        </section>
      ) : rows.length === 0 ? (
        <p>
          Inga {filter === "pending" ? "väntande" : "signerade"} mejl i kön. Detta verifierar inte
          en lyckad signerad provmottagning.
        </p>
      ) : (
        rows.map((row) => (
          <article key={row.emailId} className="rounded-xl border p-4 space-y-3">
            <div className="flex flex-wrap justify-between gap-2">
              <h2 className="font-bold break-words">{row.subject}</h2>
              <span className="text-sm">{labels[row.fetchStatus]}</span>
            </div>
            <p className="break-all">{row.from}</p>
            <p className="text-sm">
              Till: {row.to.join(", ")} · {new Date(row.receivedAt).toLocaleString("sv-SE")} ·{" "}
              {row.attachmentCount} bilagor
            </p>
            <p className="text-xs break-all">
              Provider-ID: {row.emailId} · Försök: {row.attempts}
            </p>
            {row.lastError && (
              <p role="alert" className="text-amber-800">
                {row.lastError}
              </p>
            )}
            {row.fetchStatus !== "complete" && (
              <p className="text-sm">
                Mejlkropp och avsändarautentisering är inte färdighämtade. Metadata ger inte
                tillstånd att svara.
              </p>
            )}
            <div className="flex flex-wrap gap-4">
              {row.ticketId ? (
                <Link className="underline" to="/admin/support" search={{ ticket: row.ticketId }}>
                  Öppna ärende och utkast
                </Link>
              ) : (
                <button
                  className="underline"
                  disabled={busy !== null}
                  onClick={() => action(row.emailId, false)}
                >
                  Skapa granskningsärende från metadata
                </button>
              )}
              {row.fetchStatus !== "complete" && (
                <button
                  className="underline"
                  disabled={busy !== null}
                  onClick={() => action(row.emailId, true)}
                >
                  Hämta mejlkropp från Resend
                </button>
              )}
            </div>
          </article>
        ))
      )}
    </div>
  );
}
